import {
  FC,
  useContext,
  useRef,
  createContext,
  useEffect,
  CSSProperties,
} from 'react'
import cx from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { StateValue } from 'xstate'

import { LoadingSpinner, PrimaryButton, SecondaryButton } from 'ui'
import Banner from './Navbar/Banner'

import { ChevronIcon, CloseIcon } from 'icons'

const ModuleFlowContext = createContext<{
  stepsContainerRef?: React.RefObject<HTMLDivElement>
  direction: 1 | -1
  loading?: boolean
  error?: boolean
}>({
  stepsContainerRef: undefined,
  direction: 1,
  loading: false,
  error: false,
})

export const useModuleFlow = () => {
  const context = useContext(ModuleFlowContext)
  if (context === undefined) {
    throw new Error(
      'useModuleFlow must be used within a ModuleFlowContext.Provider'
    )
  }
  return context
}

const Container: FC<{
  direction?: 1 | -1
  loading?: boolean
  error?: boolean
  hasActionBar?: boolean
}> = ({ children, direction = 1, loading, error, hasActionBar }) => {
  const stepsContainerRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation('common')

  return (
    <ModuleFlowContext.Provider
      value={{
        stepsContainerRef,
        direction,
        loading,
        error,
      }}
    >
      <div
        className={cx(
          'max-h-fill relative grid h-screen grid-rows-[minmax(0,_min-content)_auto_minmax(0,_min-content)] bg-white',
          {
            // @supports scope addresses quirk with -webkit-fill-available
            'pb-[75px] after:fixed after:inset-x-0 after:bottom-[75px] after:border-t after:border-green-1 [@supports(-webkit-touch-callout:none)]:pb-40':
              hasActionBar,
          }
        )}
      >
        {children}
        <AnimatePresence>
          {error ? (
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              className={cx('absolute left-0 w-full', {
                'bottom-[75px]': hasActionBar,
                'bottom-0': !hasActionBar,
              })}
            >
              <Banner error>{t('ui.submit-error')}</Banner>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </ModuleFlowContext.Provider>
  )
}

const Header = ({
  heading,
  backButtonOnClick,
  closeButtonHref,
  closeButtonOnClick,
  progress,
}: {
  heading: string
  backButtonOnClick: () => void
  closeButtonHref: string
  closeButtonOnClick?: () => void
  progress?: number
}) => {
  const { t } = useTranslation('common')
  return (
    <header
      className={cx(
        'relative flex items-center justify-between bg-grey-2 p-16 md:bg-transparent',
        {
          'pb-[20px]': typeof progress !== 'undefined',
        }
      )}
    >
      <button onClick={backButtonOnClick} className="md:hidden">
        <span className="sr-only">{t('ui.back')}</span>
        <ChevronIcon
          aria-hidden="true"
          className="h-24 w-24 rotate-90 stroke-grey-6"
        />
      </button>

      <h1 className="text-center text-grey-7 uppercase tracking-8 md:hidden">
        {heading}
      </h1>

      <CloseButtonBase
        href={closeButtonHref}
        className="md:ml-auto"
        onClick={closeButtonOnClick}
      />

      <AnimatePresence>
        {typeof progress !== 'undefined' ? (
          <motion.div
            initial={{ opacity: 0, scaleX: 0, transformOrigin: 'left' }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{
              type: 'tween',
              duration: 0.3,
            }}
            className="absolute inset-x-0 bottom-0 h-4 bg-green-1 md:bottom-auto md:top-0"
          >
            <div
              className="h-4 origin-left bg-teal-3 transition-transform duration-300"
              style={{ transform: `scaleX(${progress / 100})` }}
            />
            <span className="sr-only">Progress {progress}%</span>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  )
}

const CloseButtonBase = ({
  href,
  className,
  onClick,
}: {
  href: string
  className?: string
  onClick?: () => void
}) => {
  const { t } = useTranslation('common')

  return (
    <Link href={href}>
      <a className={cx(className, 'md:p-8')} onClick={onClick}>
        <span className="sr-only">{t('ui.close')}</span>
        <CloseIcon
          className="h-24 w-24 stroke-grey-6 md:h-32 md:w-32"
          aria-hidden="true"
        />
      </a>
    </Link>
  )
}

const CloseButton = ({
  href,
  onClick,
}: {
  href: string
  onClick?: () => void
}) => {
  return (
    <header>
      <CloseButtonBase
        href={href}
        className="fixed top-16 right-16 z-nav"
        onClick={onClick}
      />
    </header>
  )
}

const Steps: FC<{
  loading?: boolean
  currentStep: StateValue | string | number
}> = ({ children, currentStep }) => {
  const { stepsContainerRef, loading } = useModuleFlow()

  useEffect(() => {
    requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
      stepsContainerRef?.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    })
  }, [stepsContainerRef, currentStep])

  return (
    <motion.div
      ref={stepsContainerRef}
      tabIndex={-1}
      className="relative flex grow flex-col overflow-auto bg-white focus:!ring-0"
    >
      <AnimatePresence>
        {children}
        {loading ? (
          <motion.div
            key="loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-20 grid place-items-center bg-white bg-opacity-50 backdrop-blur-sm"
          >
            <LoadingSpinner />
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  )
}

const Step: FC<{
  motionKey: string | number
  className?: string
  contentClassName?: string
  maxWidthClassName?: string
  style?: CSSProperties
  unbound?: boolean
  overflow?: boolean
}> = ({
  children,
  motionKey,
  className,
  contentClassName,
  maxWidthClassName = 'max-w-640',
  style,
  unbound,
  overflow,
}) => {
  const { direction } = useModuleFlow()
  return (
    <div
      className={cx(
        className,
        'p-24 pb-100 sm:pl-40 sm:pt-40 sm:pr-62', {
          'h-full': !overflow
        }
      )}
      style={style}
    >
      <motion.div
        key={motionKey}
        initial={{ opacity: 0, y: direction && direction < 0 ? -32 : 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          type: 'tween',
          ease: 'easeOut',
          duration: 0.3,
        }}
        className={cx('grid h-full place-items-center', {
          [contentClassName ?? '']: unbound,
        })}
      >
        {unbound ? (
          children
        ) : (
          <div
            className={cx(contentClassName, maxWidthClassName, 'mx-auto pb-16')}
          >
            {children}
          </div>
        )}
      </motion.div>
    </div>
  )
}

const ActionBar: FC<{
  prevStepText?: string
  prevStepOnClick?: () => void
  nextStepText?: string
  nextStepOnClick?: () => void
  nextStepDisabled?: boolean
  nextStepLoading?: boolean
  nextStepType?: 'button' | 'submit'
}> = ({
  prevStepText,
  prevStepOnClick,
  nextStepText,
  nextStepOnClick,
  nextStepDisabled,
  nextStepLoading,
  nextStepType = 'button',
}) => {
  const { t } = useTranslation('common')
  const { stepsContainerRef, loading } = useModuleFlow()

  return (
    <div className="fixed inset-x-0 bottom-0 z-20 flex h-[75px] flex-row-reverse items-center justify-between bg-white px-12 xs:px-24 sm:px-48">
      {/* flex-row-reverse for more natural tab order */}
      {nextStepOnClick || nextStepType === 'submit' ? (
        <PrimaryButton
          className="ml-auto md-d:w-full"
          onClick={
            nextStepOnClick
              ? () => {
                  nextStepOnClick()
                  stepsContainerRef?.current?.focus()
                }
              : undefined
          }
          type={nextStepType}
          disabled={nextStepDisabled || loading}
          loading={nextStepLoading}
        >
          {nextStepText || t('ui.continue')}
        </PrimaryButton>
      ) : null}
      {prevStepOnClick ? (
        <SecondaryButton
          onClick={prevStepOnClick}
          className="mr-auto md-d:hidden"
        >
          {prevStepText || t('ui.back')}
        </SecondaryButton>
      ) : null}
    </div>
  )
}

export { Container, Header, CloseButton, Steps, Step, ActionBar }
